import React, { useState } from 'react';
import { getImageUrlFromAsset } from '@core/contentful/helper';
import { useMediaQuery } from '../../hooks';

// UI
import Navigation from './Navigation';
import PlayButton from '@app/marketing/components/Testimonials/PlayButton';
import VideoOverlay from '@ui/components/VideoOverlay';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade } from 'swiper';
import 'swiper/css';
import 'swiper/css/bundle';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

// Types
import { TestimonialData } from '@app/marketing/types';
import { Swiper as SwiperInstance } from 'swiper/types';

export interface Props {
    testimonials: TestimonialData[];
}

const Testimonials: React.FC<Props> = ({ testimonials }) => {
    const isDesktop = useMediaQuery('(min-width: 640px)');
    const [swiper, setSwiper] = useState(null);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [videoPlaying, setVideoPlaying] = useState<string>('');

    const slideTo = (index) => {
        if (swiper) {
            swiper.slideTo(index);
        }
    };

    const playVideo = (url: string) => setVideoPlaying(url);
    const hideVideo = () => setVideoPlaying('');

    const handleSlideChange = ({ activeIndex }: SwiperInstance) => {
        setActiveIndex(activeIndex);
    };

    return (
        <div className="mt-32" data-cy="testimonials">
            <Navigation
                onClick={slideTo}
                names={testimonials.map((testimonial) => testimonial.fields.title)}
                activeIndex={activeIndex}
            />

            <Swiper
                onSwiper={setSwiper}
                modules={[EffectFade]}
                effect={'fade'}
                onSlideChange={handleSlideChange}
            >
                {testimonials.map((testimonial, index) => {
                    const { color, textColor, background, logo, videoUrl, title, quote, author } =
                        testimonial.fields;

                    return (
                        <SwiperSlide key={testimonial.sys.id}>
                            <div
                                className="h-[580px] w-full rounded-3xl bg-cover text-left sm:h-[450px]"
                                style={{
                                    backgroundColor: color,
                                    color: textColor,
                                }}
                            >
                                <div className="flex h-full flex-col sm:flex-row">
                                    <div className="flex flex-1 flex-col justify-between p-8 sm:p-16 sm:pr-0">
                                        <div
                                            className="relative h-8 w-60 bg-cover bg-center bg-no-repeat"
                                            style={{
                                                backgroundImage: `url(${getImageUrlFromAsset(logo)})`,
                                            }}
                                        />
                                        <p className="text-xl font-medium sm:my-0 sm:text-3xl sm:font-semibold">
                                            {quote}
                                        </p>
                                        <p className="text-sm sm:text-xl">{author}</p>
                                    </div>
                                    <div className="relative flex flex-1">
                                        <div
                                            className="absolute left-0 top-0 z-10 h-32 w-full sm:h-full sm:w-32"
                                            style={{
                                                background: `linear-gradient(${
                                                    isDesktop ? '90deg' : '180deg'
                                                }, ${color} 0%, transparent 100%)`,
                                            }}
                                        />
                                        <div
                                            className="relative h-full w-full rounded-b-3xl bg-cover bg-center bg-no-repeat sm:rounded-r-3xl"
                                            style={{
                                                backgroundImage: `url(${getImageUrlFromAsset(background)})`,
                                            }}
                                        />

                                        {videoUrl && (
                                            <PlayButton url={videoUrl} onClick={playVideo} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                    );
                })}
            </Swiper>

            {videoPlaying && <VideoOverlay url={videoPlaying} onClose={hideVideo} />}
        </div>
    );
};

export default Testimonials;
